import * as z from "zod"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { Control, useController, useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "react-i18next"
import { Check } from "lucide-react"
import { Label } from "@/components/ui/label"
import { FormField } from "@/components/ui/form-field"
import {
  ControlledRadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { Input } from "@/components/ui/input"
import { Combobox, SelectOption } from "@/components/ui/combobox"
import { CommandItem } from "@/components/ui/command"
import { Badge } from "@/components/ui/badge"
import { usePendingVisit } from "@/lib/page-rendering"
import { Button } from "@/components/ui/button"

const schema = z.object({
  shortlist: z.discriminatedUnion("_create", [
    z.object({
      id: z.string(VALIDATION_ERROR_KEYS.REQUIRED),
      _create: z.literal("false"),
    }),
    z.object({
      name: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
      _create: z.literal("true"),
    }),
  ]),
})

export type ShortlistableItemFormSchema = z.infer<typeof schema>

// A shortlist the user can choose; `saved` marks one that already holds the
// item being saved.
export type ShortlistOption = SelectOption<string> & { saved: boolean }

export function ShortlistableItemForm({
  shortlists,
  onSubmit,
  newLabel,
  newDescription,
  existingDescription,
  existingLabel,
  saveLabel,
}: {
  shortlists: ShortlistOption[]
  onSubmit: (data: ShortlistableItemFormSchema) => void
  newLabel: string
  newDescription: string
  existingLabel: string
  existingDescription: string
  saveLabel: string
}) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ShortlistableItemFormSchema>({
    resolver: zodResolver(schema),
    // Adding to a shortlist the user already has is the common case once they
    // have one; naming a new shortlist is the only choice before then.
    defaultValues: {
      shortlist: {
        _create: shortlists.length > 0 ? "false" : "true",
      },
    },
  })
  const creating = useWatch({ control, name: "shortlist._create" }) === "true"
  const pending = usePendingVisit()
  const { t } = useTranslation("shortlists")

  return (
    <form
      data-testid="shortlist-form"
      className="mt-6 space-y-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormField
        label={t("shortlistable_item_form.shortlist_type")}
        name="shortlist._create"
        control={control}
        errors={errors}
      >
        <ControlledRadioGroup>
          <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
            <div className="flex flex-row items-center">
              <RadioGroupItem
                value="true"
                id="new-shortlist"
                className="peer"
                aria-labelledby="new-shortlist-label"
                aria-describedby="new-shortlist-description"
              />
              <Label
                htmlFor="new-shortlist"
                className="flex items-center gap-2 rounded-md px-4 py-2"
              >
                <div className="flex flex-col">
                  <span className="font-medium" id="new-shortlist-label">
                    {newLabel}
                  </span>
                  <span className="text-xs" id="new-shortlist-description">
                    {newDescription}
                  </span>
                </div>
              </Label>
            </div>
            {creating && (
              <FormField
                errors={errors}
                {...register("shortlist.name")}
                label={t("shortlistable_item_form.name.label")}
              >
                <Input />
              </FormField>
            )}
          </div>

          <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
            <div className="flex flex-row items-center">
              <RadioGroupItem
                value="false"
                id="existing-shortlist"
                className="peer"
                disabled={shortlists.length === 0}
                aria-labelledby="existing-shortlist-label"
                aria-describedby="existing-shortlist-description"
              />
              <Label
                htmlFor="existing-shortlist"
                className="flex items-center gap-2 rounded-md px-4 py-2"
              >
                <div className="flex flex-col">
                  <span className="font-medium" id="existing-shortlist-label">
                    {existingLabel}
                  </span>
                  <span className="text-xs" id="existing-shortlist-description">
                    {existingDescription}
                  </span>
                </div>
              </Label>
            </div>

            {!creating && (
              <FormField
                name="shortlist.id"
                control={control}
                label={t("shortlistable_item_form.select_shortlist")}
                errors={errors}
              >
                <ShortlistCombobox
                  options={shortlists}
                  savedLabel={t("shortlistable_item_form.saved")}
                />
              </FormField>
            )}
          </div>
        </ControlledRadioGroup>
      </FormField>
      <Button type="submit" className="w-full" disabled={pending}>
        {saveLabel}
      </Button>
    </form>
  )
}

// The shortlist choice, marking each option that already holds the item so
// the user sees where it is saved before choosing again. Takes the field
// binding the enclosing form field injects.
function ShortlistCombobox({
  name,
  control,
  options,
  savedLabel,
  ...props
}: Omit<
  React.ComponentProps<typeof Combobox<string, ShortlistOption>>,
  "value" | "onChange" | "renderComboboxItem"
> & {
  name?: "shortlist.id"
  control?: Control<ShortlistableItemFormSchema>
  savedLabel: string
}) {
  const { field } = useController({ name: name!, control })

  return (
    <Combobox
      {...props}
      options={options}
      value={field.value}
      onChange={(value) => field.onChange(value)}
      renderComboboxItem={({ option, key, setOpen }) => (
        <CommandItem
          key={option.value}
          className="flex-wrap"
          keywords={[key, option.value]}
          onSelect={() => {
            field.onChange(field.value === option.value ? null : option.value)
            setOpen(false)
          }}
        >
          {option.label}
          {option.saved && (
            // The space keeps the marker a separate word in the option's
            // text; flex layout renders no gap for it.
            <>
              {" "}
              <Badge variant="secondary" className="ml-auto">
                <Check aria-hidden />
                {savedLabel}
              </Badge>
            </>
          )}
        </CommandItem>
      )}
    />
  )
}
